import React, {
  FC,
  useState,
  useCallback,
  memo,
  useMemo,
}                             from 'react';
import { FormikConfig }       from 'formik';
import { IFeeValues }         from '@typings/accountManagement';
import {
  IBulkPricingList,
  IBulkCuidsFees,
}                             from '@typings/invoiceManagement';
import { EAccountStatus }     from '@enums/accountManagement';
import { useToggle }          from '@utils/useToggle';
import { DefaultPricingPage } from '@modules/invoiceManagement/pages/DefaultPricingPage';
import {
  useFetchPricing,
  useUpdateBulkFees,
  useUpdatePricing,
}                             from '@modules/invoiceManagement/queries/invoiceManagementQueries';
import { useFetchAccounts }   from '@modules/accountManagement/queries/accountQueries';
import { ICheckboxOption }    from '@components/Checkbox/CheckboxFieldGroup';
import { Loader }             from '@components/Loader/Loader';

export const DefaultPricing: FC = () => {
  const [defaultPricing, setDefaultPricing] = useState<IFeeValues[]>([] as IFeeValues[]);
  const [isEditing, toggleIsEditing]        = useToggle();

  // #region API INTEGRATION
  const { isFetching } = useFetchPricing({
    onSuccess: (data) => setDefaultPricing(data),
  });

  const { mutateAsync: savePricing, isLoading } = useUpdatePricing({
    onSuccess: (data) => setDefaultPricing(data),
  });

  const {
    mutateAsync: updateBulkFees,
    isLoading  : isLoadingBulkFees,
  } = useUpdateBulkFees();

  const { data: cuList = [] } = useFetchAccounts('');
  // #endregion

  const organizationsOptions: ICheckboxOption[] = useMemo(
    () => cuList
      .filter((CU) => CU.status === EAccountStatus.Active)
      .map((CU) => ({
        label : `${CU.name} (${CU.cuid})`,
        name  : CU.cuid,
        value : true,
      })), [cuList],
  );

  const handleUpdateBulkFees = useCallback(
    (feesValues: IFeeValues[], modalValues: IBulkPricingList) => {
      const keys = Object.keys(modalValues.fees).filter((key) => modalValues.fees[key] === true);
      const fees = keys.map((item) => {
        const [feeType, feeUserType] = item.split('-').map((el) => Number(el));

        return feesValues.find((el) => el.type === feeType && el.userType === feeUserType);
      }).filter((item) => !!item) as IFeeValues[];

      const result: IBulkCuidsFees = {
        fees,
        cuiDs: Object.keys(
          Object.fromEntries(
            Object.entries(modalValues.accounts)
              .filter(([, value]) => value === true),
          ),
        ),
      };

      updateBulkFees(result);
    }, [updateBulkFees],
  );

  const onSaveChanges = useCallback<FormikConfig<IFeeValues[]>['onSubmit']>(
    async (values): Promise<void> => {
      await savePricing(values);
      toggleIsEditing();
    },
    [savePricing, toggleIsEditing],
  );

  const onResetForm = useCallback<NonNullable<FormikConfig<IFeeValues[]>['onReset']>>(
    () => {
      toggleIsEditing();
    },
    [toggleIsEditing],
  );

  if (isFetching) {
    return <Loader />;
  }

  return (
    <DefaultPricingPage
      isEditing            = {isEditing}
      isLoading            = {isFetching || isLoading || isLoadingBulkFees}
      defaultPricing       = {defaultPricing}
      organizationsOptions = {organizationsOptions}
      toggleIsEditing      = {toggleIsEditing}
      onSaveChanges        = {onSaveChanges}
      onResetForm          = {onResetForm}
      onUpdateBulkFees     = {handleUpdateBulkFees}
    />
  );
};

export const DefaultPricingContainer = memo(DefaultPricing);
